import { createRouter, createWebHistory, RouteLocationNormalizedLoadedGeneric, RouteRecordRaw } from 'vue-router';
import { getCurrentUser } from 'vuefire';
import { notifyError } from './helpers/notification.helper';

const OverviewComponent = () => import('../pages/Overview.vue');
const CinemaComponent = () => import('../pages/Cinema/Cinema.vue');
const WatchlistComponent = () => import('../pages/Watchlist/Watchlist.vue');
const MyPartnerComponent = () => import('../pages/MyPartner/MyPartner.vue');
const UserComponent = () => import('../pages/user/User.vue');
const UserProfileComponent = () => import('../pages/user/pages/UserProfile.vue');
const UserFiltersComponent = () => import('../pages/user/pages/UserFilters.vue');

const MovieDetailComponent = () => import('../pages/Details/MovieDetails.vue');
const SerieDetailComponent = () => import('../pages/Details/SerieDetails.vue');

const SeasonsComponent = () => import('../pages/Seasons/Seasons.vue');
const SeasonComponent = () => import('../pages/Seasons/Season.vue');

const MediaComponent = () => import('../pages/Media/Media.vue');
const MediaVideosComponent = () => import('../pages/Media/components/MediaVideos.vue');
const MediaPostersComponent = () => import('../pages/Media/components/MediaPosters.vue');
const MediaBackgroundsComponent = () => import('../pages/Media/components/MediaBackgrounds.vue');

const CastDetailsComponent = () => import('../pages/Cast/Cast.vue');
const CastCastComponent = () => import('../pages/Cast/components/CastCast.vue');
const CastCrewComponent = () => import('../pages/Cast/components/CastCrew.vue');

const PeopleDetailsComponent = () => import('../pages/People/People.vue');

const AdminComponent = () => import('../pages/admin/Admin.vue');

const LoginComponent = () => import('../pages/Login.vue');

export const routes: Array<RouteRecordRaw> = [
   {
      path: '/',
      component: OverviewComponent,
      meta: { requiresAuth: true },
      children: [
         {
            path: '',
            alias: '/cinema',
            name: 'Home',
            component: CinemaComponent,
         },
         {
            path: 'watchlist',
            name: 'Watchlist',
            component: WatchlistComponent,
         },
         {
            path: 'my-partner',
            name: 'my-partner',
            component: MyPartnerComponent,
         },
         {
            path: 'user',
            name: 'User',
            redirect: '/user/profile',
            component: UserComponent,
            children: [
               {
                  path: 'profile',
                  name: 'UserProfile',
                  component: UserProfileComponent,
               },
               {
                  path: 'filters',
                  name: 'UserFilters',
                  component: UserFiltersComponent,
                  beforeEnter: (to, from, next) => {
                     next('/user/profile');
                  },
               },
            ],
         },
         {
            path: 'movies/:id',
            name: 'MovieDetails',
            component: MovieDetailComponent,
         },
         {
            path: 'series/:id',
            name: 'SerieDetails',
            component: SerieDetailComponent,
         },
         {
            path: 'seasons/:serieId',
            name: 'SeriesSeasons',
            component: SeasonsComponent,
         },
         {
            path: 'seasons/:serieId/season/:seasonNumber',
            name: 'SeasonDetails',
            component: SeasonComponent,
         },
         {
            path: ':cinemaType/:cinemaId/media',
            name: 'MediaDetails',
            redirect: { name: 'MediaVideos' },
            component: MediaComponent,
            children: [
               {
                  path: 'videos',
                  name: 'MediaVideos',
                  component: MediaVideosComponent,
               },
               {
                  path: 'posters',
                  name: 'MediaPosters',
                  component: MediaPostersComponent,
               },
               {
                  path: 'backgrounds',
                  name: 'MediaBackgrounds',
                  component: MediaBackgroundsComponent,
               },
            ],
         },
         {
            path: ':cinemaType/:cinemaId/casts',
            name: 'CastDetails',
            redirect: { name: 'CastCast' },
            component: CastDetailsComponent,
            children: [
               {
                  path: 'cast',
                  name: 'CastCast',
                  component: CastCastComponent,
               },
               {
                  path: 'crew',
                  name: 'CastCrew',
                  component: CastCrewComponent,
               },
            ],
         },
         {
            path: 'people/:id',
            name: 'PeopleDetails',
            component: PeopleDetailsComponent,
         },

         // {
         //    path: 'casts/:cinemaId',
         //    name: 'CastDetails',
         //    component: SerieDetailComponent,
         //    children: [
         //       {
         //       path: 'cast/:castId',
         //       name: 'CastDetails',
         //       component: SerieDetailComponent,
         //       },
         //    ],
         // },
         // {
         //    path: 'media/:cinemaId',
         //    name: 'MediaDetails',
         //    component: SerieDetailComponent,
         //    children: [
         //       {
         //          path: 'videos',
         //          name: 'MediaVideos',
         //          component: SerieDetailComponent,
         //       },
         //       {
         //          path: 'posters',
         //          name: 'MediaPosters',
         //          component: SerieDetailComponent,
         //       },
         //       {
         //          path: 'backgrounds',
         //          name: 'MediaBackgrounds',
         //          component: SerieDetailComponent,
         //       },
         //    ],
         // },
      ],
   },
   {
      path: '/login',
      component: LoginComponent,
      meta: { requiresAuth: false },
   },
   {
      path: '/admin',
      component: AdminComponent,
      meta: { requiresAuth: true },
   },
   {
      path: '/:pathMatch(.*)*',
      redirect: '/',
      meta: { requiresAuth: true },
   },
];

const scrollPositions = new Map<string, Record<string, number>>();
export const router = createRouter({
   history: createWebHistory(),
   routes,
   scrollBehavior(to) {
      const position = scrollPositions.get(to.fullPath) || { left: 0, top: 0 };
      return new Promise((resolve) => {
         setTimeout(() => {
            const container = document.querySelector('.scroll-container');
            container?.scrollTo(position.left, position.top);
            resolve(position);
         });
      });
   },
});

function setScrollPosition(from: RouteLocationNormalizedLoadedGeneric) {
   const container = document.querySelector('.scroll-container');

   if (container) {
      const scrollY = container.scrollTop;
      const scrollX = container.scrollLeft;
      scrollPositions.set(from.fullPath, { left: scrollX, top: scrollY });
   }
}

router.beforeEach(async (to, from, next) => {
   setScrollPosition(from);
   const currentFirebaseUser = await getCurrentUser();

   if (to.meta.requiresAuth) {
      if (!currentFirebaseUser) return next({ path: '/login' });
   } else {
      if (currentFirebaseUser) return next({ path: '/' });
   }

   return next();
});

router.afterEach((to, from, failure) => {
   if (failure) {
      notifyError({ title: 'Something went wrong while navigating' });
   }
});
